import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { QrCodeIcon, MessageCircle, MapPin, Mail, Phone, Send, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

export default function FinderPage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [qrCodeInfo, setQrCodeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageSent, setMessageSent] = useState(false);
  const [includeLocation, setIncludeLocation] = useState(false);
  const [location, setLocation] = useState(null);
  
  // Claiming state
  const [isClaiming, setIsClaiming] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (uid) {
      fetchQRCodeInfo();
    }
  }, [uid]);

  const fetchQRCodeInfo = async () => {
    try {
      const response = await api.getQRCodeInfo(uid.toUpperCase());
      setQrCodeInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch QR code info:', error);
      setError(error.response?.data?.detail || 'QR code not found');
    } finally {
      setLoading(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy
          });
          toast.success('Location captured!');
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Could not get your location. Please enable location services.');
        }
      );
    } else {
      toast.error('Location services not supported by your browser.');
    }
  };

  const handleLocationToggle = (checked) => {
    setIncludeLocation(checked);
    if (checked && !location) {
      getCurrentLocation();
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    setSendingMessage(true);

    const formData = new FormData(event.target);
    const message = formData.get('message');
    const email = formData.get('email') || null;

    const messageData = {
      content: message,
      sender_email: email,
      sender_phone: null,  // Phone disabled until Twilio is functional
      location_data: includeLocation && location ? location : null
    };

    try {
      await api.sendMessageToOwner(uid.toUpperCase(), messageData);
      setMessageSent(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleClaimQR = async (event) => {
    event.preventDefault();
    setIsClaiming(true);

    const formData = new FormData(event.target);
    const claimData = {
      owner_name: formData.get('owner_name'),
      owner_email: formData.get('owner_email'),
      title: formData.get('title'),
      greeting_message: formData.get('greeting_message') || null,
      password: formData.get('password'),
      allow_reply_contact: formData.get('allow_reply_contact') === 'on'
    };

    try {
      console.log('Claiming QR code:', uid);
      const response = await api.claimQRCodePublic(uid.toUpperCase(), claimData);
      
      console.log('Claim response:', response.data);
      
      // Auto-login with the token
      login(response.data.access_token);
      
      toast.success('QR code claimed successfully! Redirecting to dashboard...');
      
      // Redirect to dashboard
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 1500);
      
    } catch (error) {
      console.error('Failed to claim QR code:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to claim QR code. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="p-8 text-center">
            <div className="animate-spin w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading QR code information...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardHeader className="text-center pt-8">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <QrCodeIcon className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent" data-testid="error-title">
              QR Code Not Found
            </CardTitle>
            <CardDescription className="text-gray-600 mt-2" data-testid="error-description">{error}</CardDescription>
          </CardHeader>
          <CardContent className="pb-8">
            <Button 
              onClick={() => window.location.href = '/'} 
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!qrCodeInfo?.is_claimed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        {/* Branded Header */}
        <header className="bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <QrCodeIcon className="h-6 w-6 text-white" />
                </div>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  iComment.app
                </span>
              </div>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/'}
                className="border-blue-200 hover:bg-blue-50"
              >
                Home
              </Button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <div className="relative py-12 px-4 overflow-hidden">
          {/* Animated background elements */}
          <div className="absolute top-10 right-10 w-32 h-32 bg-blue-300 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-purple-300 rounded-full opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
          
          <div className="max-w-2xl mx-auto text-center mb-8 relative z-10">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-6">
              <span className="text-blue-700 font-semibold text-sm">🎉 Congratulations! You found a QR code</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Claim This QR Code
              </span>
            </h1>
            <p className="text-gray-600 text-lg max-w-xl mx-auto">
              Set up this QR code so people can contact you instantly when they find your items
            </p>
          </div>

          {/* Claim Form Card */}
          <div className="flex items-center justify-center px-4">
            <Card className="w-full max-w-lg shadow-2xl border-0 bg-white/80 backdrop-blur-sm">
              <CardHeader className="text-center pb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 transform hover:scale-110 transition-transform duration-300 shadow-lg">
                  <QrCodeIcon className="w-10 h-10 text-white" />
                </div>
                <CardDescription className="text-gray-600">
                  Fill in your details below to activate this QR code
                </CardDescription>
              </CardHeader>
              
              <CardContent className="pb-8">
                <form onSubmit={handleClaimQR} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="owner_name" className="text-gray-700 font-medium">Your Name *</Label>
                    <Input
                      id="owner_name"
                      name="owner_name"
                      type="text"
                      placeholder="John Doe"
                      className="border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="owner_email" className="text-gray-700 font-medium">Your Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                      <Input
                        id="owner_email"
                        name="owner_email"
                        type="email"
                        placeholder="your@email.com"
                        className="pl-11 border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 flex items-center">
                      <MessageCircle className="w-3 h-3 mr-1" />
                      You'll receive notifications when someone contacts you
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Create a Password *</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a secure password"
                        required
                        minLength={6}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">
                      If you already have an account, use your existing password
                    </p>
                  </div>

                  <div className="border-t pt-4 mt-4">
                    <h3 className="font-medium text-gray-900 mb-3">Item Details</h3>
                    
                    <div className="space-y-2">
                      <Label htmlFor="title">Item Title *</Label>
                      <Input
                        id="title"
                        name="title"
                        type="text"
                        placeholder="e.g., My Wallet, Laptop Bag, Keys"
                        required
                      />
                    </div>

                    <div className="space-y-2 mt-3">
                      <Label htmlFor="greeting_message">Greeting Message (Optional)</Label>
                      <Textarea
                        id="greeting_message"
                        name="greeting_message"
                        placeholder="e.g., Thanks for finding my item! Please use this to contact me."
                        rows={3}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg mt-4">
                      <div className="space-y-0.5 flex-1">
                        <Label htmlFor="allow_reply_contact" className="text-sm font-medium">
                          Allow finders to provide contact info
                        </Label>
                        <p className="text-xs text-gray-600">
                          Let people who message you provide their email to receive replies through iComment.app
                        </p>
                      </div>
                      <Switch
                        id="allow_reply_contact"
                        name="allow_reply_contact"
                        defaultChecked={true}
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={isClaiming}
                  >
                    {isClaiming ? 'Claiming...' : 'Claim QR Code & Create Account'}
                  </Button>

                  <p className="text-xs text-center text-gray-500">
                    By claiming this QR code, you'll create a free account to manage your items
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (messageSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageCircle className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-green-600" data-testid="success-title">Message Sent!</CardTitle>
            <CardDescription data-testid="success-description">
              Your message has been delivered to the owner. If you provided contact information, they can reply to you.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button 
              onClick={() => setMessageSent(false)}
              variant="outline"
              className="w-full"
              data-testid="send-another-btn"
            >
              Send Another Message
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <QrCodeIcon className="h-8 w-8 text-blue-600 mr-3" />
            <span className="text-2xl font-bold text-gray-900">iComment.app</span>
          </div>
        </div>
      </header>

      <div className="flex items-center justify-center p-4 pt-8">
        <Card className="w-full max-w-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl" data-testid="item-title">{qrCodeInfo.title}</CardTitle>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Greeting Message */}
            {qrCodeInfo.greeting_message && (
              <div className="bg-blue-50 p-4 rounded-lg" data-testid="greeting-message">
                <p className="text-gray-700">{qrCodeInfo.greeting_message}</p>
              </div>
            )}

            {/* Greeting Image */}
            {qrCodeInfo.greeting_image && (
              <div className="text-center" data-testid="greeting-image">
                <img 
                  src={qrCodeInfo.greeting_image} 
                  alt="Owner's greeting" 
                  className="max-w-full h-auto rounded-lg mx-auto"
                />
              </div>
            )}

            {/* Message Form */}
            <form onSubmit={handleSendMessage} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="message">Your Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Type your message here..."
                  required
                  rows={4}
                  data-testid="message-input"
                />
              </div>

              <div className="space-y-4">
                {qrCodeInfo?.allow_reply_contact ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-900">
                      <Mail className="inline h-4 w-4 mr-2" />
                      Enter your contact information below if you would like to receive a reply to this message. 
                      Your contact information is NOT shared and all messages will go through the iComment.app
                    </p>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-900">
                      <EyeOff className="inline h-4 w-4 mr-2" />
                      You will not be able to receive a reply to this message as the feature has been disabled by the owner.
                    </p>
                  </div>
                )}
                
                <h4 className="text-sm font-medium text-gray-700">
                  Contact Information (Optional)
                </h4>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your@email.com"
                      className="pl-10"
                      data-testid="email-input"
                    />
                  </div>
                  <p className="text-xs text-gray-500">
                    Provide your email to allow the owner to reply through iComment.app
                  </p>
                </div>
              </div>

              {/* Location Sharing */}
              {qrCodeInfo.location_enabled && (
                <div className="flex items-center space-x-2" data-testid="location-toggle">
                  <Switch
                    id="location"
                    checked={includeLocation}
                    onCheckedChange={handleLocationToggle}
                    data-testid="location-switch"
                  />
                  <Label htmlFor="location" className="flex items-center cursor-pointer">
                    <MapPin className="h-4 w-4 mr-1 text-gray-500" />
                    Share my current location
                  </Label>
                </div>
              )}

              {includeLocation && location && (
                <div className="text-sm text-green-600 bg-green-50 p-2 rounded" data-testid="location-status">
                  📍 Location captured (accuracy: {Math.round(location.accuracy)}m)
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={sendingMessage}
                data-testid="send-message-btn"
              >
                {sendingMessage ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>

            <div className="text-xs text-gray-500 text-center space-y-1" data-testid="privacy-notice">
              <p>🔒 Your message will be sent anonymously</p>
              <p>Contact info is only shared if you choose to provide it</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}